import { ref } from 'vue'
import store from '@/store'
import { config } from '@/config.js'

/**
 * useCambiarAsientoSecciones
 * Composable that manages section navigation state and API calls
 * for the CambiarAsiento modal when the venue has sections (tipo_proceso_online == 2).
 *
 * Mirrors the pattern from SeleccionarRegionConSecciones.vue.
 *
 * @param {Object} options
 * @param {Ref} options.recintoId - recinto ID
 * @param {Ref} options.listaId - lista abonados ID
 * @returns {Object}
 */
export function useCambiarAsientoSecciones({ recintoId, listaId }) {
    const secciones = ref([])
    const seccionSeleccionada = ref(null)
    const zonas = ref([])
    const breadcrumbs = ref([{ name: 'Recinto' }])
    const loading = ref(false)
    const cargandoZonas = ref(false)
    const error = ref(null)

    /**
     * Fetch top-level sections for the venue
     */
    async function getSecciones() {
        loading.value = true
        error.value = null

        try {
            const res = await store.dispatch(config.getVuexAlias('back.abonos.taquilla.recinto.secciones'), {
                filters: {},
                recinto_id: recintoId.value,
                lista_id: listaId.value
            })
            secciones.value = res.data.data
        } catch (err) {
            console.error('Error fetching secciones:', err)
            error.value = 'Error al cargar las secciones'
        } finally {
            loading.value = false
        }
    }

    /**
     * Select a section and fetch its regions/zones
     * @param {Object} seccion
     */
    async function selectSeccion(seccion) {
        breadcrumbs.value.push({
            name: seccion.name,
            id: seccion.id,
            tiene_secciones: seccion.tiene_secciones
        })
        cargandoZonas.value = true
        zonas.value = []
        seccionSeleccionada.value = seccion

        await getSeccionRegiones(seccion)
    }

    /**
     * Fetch regions/zones within a section
     * @param {Object} seccion
     */
    async function getSeccionRegiones(seccion) {
        cargandoZonas.value = true
        error.value = null

        const params = {
            filters: {},
            seccion_id: seccion.id,
            recinto_id: recintoId.value,
            lista_id: listaId.value
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.abonos.taquilla.recinto.secciones.regiones'),
                params
            )
            if (!seccion.tiene_secciones) {
                zonas.value = zonas.value.concat(res.data)
            } else {
                secciones.value = res.data.data
            }
        } catch (err) {
            console.error('Error fetching seccion regiones:', err)
            error.value = 'Error al cargar las zonas de la seccion'
        } finally {
            cargandoZonas.value = false
        }
    }

    /**
     * Navigate via breadcrumb click
     * @param {Object} item
     * @param {number} index
     */
    function goTo(item, index) {
        if (item.name === 'Recinto') {
            reset()
        } else {
            breadcrumbs.value.splice(index)
            selectSeccion(item)
        }
    }

    /**
     * Go back one level
     */
    function volver() {
        if (breadcrumbs.value.length > 1) {
            const index = breadcrumbs.value.length - 2
            goTo(breadcrumbs.value[index], index)
        }
    }

    /**
     * Reset to initial section list view
     */
    function reset() {
        breadcrumbs.value = [{ name: 'Recinto' }]
        seccionSeleccionada.value = null
        zonas.value = []
        getSecciones()
    }

    // Initialize
    getSecciones()

    return {
        secciones,
        seccionSeleccionada,
        zonas,
        breadcrumbs,
        loading,
        cargandoZonas,
        error,
        getSecciones,
        selectSeccion,
        getSeccionRegiones,
        goTo,
        volver,
        reset
    }
}
